import { t } from '@trimble/i18n';
import {
  MultiSelectProps,
  SelectionTree,
  SelectionTreeProps,
  type SelectionTreeRowMode,
  SingleSelectProps,
  TreeItemContextMenuOption,
  SelectionTreeVisibleContentRenderer,
  TreeNodeDefinition,
} from '@trimble/ui-components/solidjs';
import { createEffect, createMemo, JSX, onCleanup } from 'solid-js';
import { createStore } from 'solid-js/store';
import { LoadableItems, LoadState } from '@trimble/data-loading';
import { PointcloudStatus } from '@data-access/geospatial/scan-data';
import {
  createProjectDataSelection,
  FilterHandlerRecord,
  ProjectDataPointCloudsSectionOperations,
  useProjectDataPanel,
} from '@trimble/project-data-panel-ui';
import {
  CrsIssueKind,
  isMissionPointCloud,
  PointCloudItem,
} from '@trimble/project-data-panel-types';
import { mapCrsIssueToTreeNodeStatus } from './crs-issue-status';
import { mapPointCloudStatusToTreeNodeStatus } from './map-point-cloud-status-to-tree-node-status';

const ROOT_NODE_ID = 'pointClouds';
const POINT_CLOUDS_LABEL_ICON = 'svg-point-cloud';

export interface PointCloudsTreeProps
  extends ProjectDataPointCloudsSectionOperations {
  pointClouds: LoadableItems<PointCloudItem>;
  visibleContent?: SelectionTreeVisibleContentRenderer<PointCloudItem>;
  rowMode?: SelectionTreeRowMode;
  multiSelect?: boolean;
  defaultExpandIds?: string[];
  /** Per-cloud CRS warning shown under the label when ingest status is ready. */
  getCrsIssue?(itemId: string): CrsIssueKind | undefined;
}

export const PointCloudsTree = (props: PointCloudsTreeProps): JSX.Element => {
  const {
    filterText,
    sortBy,
    sortDirection,
    filterMode,
    selection: panelSelection,
  } = useProjectDataPanel();
  const selection = panelSelection ?? createProjectDataSelection();

  const [selectionTreeProps, setSelectionTreeProps] = createStore<
    Pick<SelectionTreeProps<PointCloudItem>, 'childrenLoadStatus' | 'nodes'>
  >({
    childrenLoadStatus: 'pending',
    nodes: [],
  });

  const isPointCloudNodeLoading = (nodeId: string): boolean => {
    return nodeId === ROOT_NODE_ID ? false : props.isNodeLoading(nodeId);
  };

  const isPointCloudNodeVisible = (nodeId: string): boolean => {
    return nodeId === ROOT_NODE_ID ? false : props.isNodeVisible(nodeId);
  };

  const isPointCloudNodeDisabled = (nodeId: string): boolean => {
    const pointClouds = props.pointClouds;
    if (pointClouds.state !== LoadState.LoadComplete) return false;
    const item = pointClouds.items[nodeId];
    // eslint-disable-next-line sonarjs/different-types-comparison -- index access can be undefined at runtime (noUncheckedIndexedAccess is off)
    return item !== undefined && item.status !== PointcloudStatus.Ready;
  };

  const ownsPointCloudNode = (nodeId: string): boolean => {
    const pointClouds = props.pointClouds;
    return (
      nodeId !== ROOT_NODE_ID &&
      pointClouds.state === LoadState.LoadComplete &&
      nodeId in pointClouds.items
    );
  };

  const sectionHandle = selection.registerSection({
    ownsNode: ownsPointCloudNode,
    isNodeVisible: isPointCloudNodeVisible,
    onVisibilityToggle: (nodeId) => props.onVisibilityToggle(nodeId),
    isNodeDisabled: isPointCloudNodeDisabled,
    isNodeLoading: isPointCloudNodeLoading,
  });
  onCleanup(() => sectionHandle.dispose());

  const selectionProps = createMemo<
    SingleSelectProps<PointCloudItem> | MultiSelectProps
  >(() =>
    props.multiSelect
      ? {
          multiSelect: true,
          isNodeSelected: selection.isNodeSelected,
          onSelectionChange: (ids) => sectionHandle.applySelection(ids),
        }
      : {
          multiSelect: false,
          isNodeSelected: props.isNodeSelected,
          onNodeSelect: props.onNodeSelect,
        }
  );

  const createContextMenuOptions = (
    item: PointCloudItem
  ): TreeItemContextMenuOption[] => {
    const options: TreeItemContextMenuOption[] = [
      {
        text: t('siteVisualization.sidePanelLayout.fitView'),
        onClick: () => {
          props.onFitToView?.(item);
        },
        disabled: () =>
          !isPointCloudNodeVisible(item.id) || isPointCloudNodeLoading(item.id),
      },
      {
        text: t('siteVisualization.sidePanelLayout.showOnly'),
        onClick: () => {
          props.onShowOnly?.(item);
        },
      },
    ];

    if (props.onSeeDetails && isMissionPointCloud(item)) {
      options.push({
        text: t('siteVisualization.sidePanelLayout.seeDetails'),
        onClick: () => {
          props.onSeeDetails?.(item);
        },
        disabled: () =>
          !isPointCloudNodeVisible(item.id) || isPointCloudNodeLoading(item.id),
      });
    }

    if (props.onRenderingSettings) {
      options.push({
        text: t('siteVisualization.sidePanelLayout.options'),
        onClick: () => {
          props.onRenderingSettings?.(item);
        },
        disabled: () =>
          !isPointCloudNodeVisible(item.id) || isPointCloudNodeLoading(item.id),
      });
    }

    const trailingOptions = props.getTrailingContextMenuOptions?.(item);
    if (trailingOptions?.length) {
      options.push(...trailingOptions);
    }

    return options;
  };

  const treeNodes = createMemo((): TreeNodeDefinition<PointCloudItem>[] => {
    const pointClouds = props.pointClouds;
    if (pointClouds.state !== LoadState.LoadComplete) return [];

    const items = Object.values(pointClouds.items);
    const toggleTooltip = t(
      'mobileMapping.projectData.togglePointCloudVisibility'
    );

    const rootNode: TreeNodeDefinition<PointCloudItem> = {
      id: ROOT_NODE_ID,
      label: t('mobileMapping.projectData.pointClouds'),
      isLeaf: false,
      labelIcon: POINT_CLOUDS_LABEL_ICON,
    };

    const itemNodes = items.map(
      (item) =>
        ({
          id: item.id,
          label: item.name,
          data: item,
          parentId: ROOT_NODE_ID,
          isLeaf: true,
          status:
            mapPointCloudStatusToTreeNodeStatus(item.status) ??
            mapCrsIssueToTreeNodeStatus(props.getCrsIssue?.(item.id)),
          toggleTooltip,
          contextMenuOptions: createContextMenuOptions(item),
        }) satisfies TreeNodeDefinition<PointCloudItem>
    );

    return [rootNode, ...itemNodes];
  });

  const filterHandlers: FilterHandlerRecord<PointCloudItem> = {
    all: { filter: () => true },
    active: { filter: (node) => isPointCloudNodeVisible(node.id) },
    archived: { filter: () => false },
  };

  createEffect(() => {
    const pointClouds = props.pointClouds;
    const nodes = treeNodes();
    setSelectionTreeProps({
      childrenLoadStatus:
        pointClouds.state === LoadState.LoadComplete ? 'success' : 'pending',
      nodes,
    });
  });

  return (
    <div
      class="point-clouds-tree w-full"
      data-testid="point-cloud-selection-tree"
    >
      <SelectionTree
        {...selectionTreeProps}
        visibleContent={props.visibleContent}
        rowMode={props.rowMode}
        sortBy={sortBy()}
        sortDirection={sortDirection()}
        filterText={filterText()}
        filterMode={filterMode()}
        filterHandlers={filterHandlers}
        defaultExpandIds={props.defaultExpandIds}
        isNodeLoading={isPointCloudNodeLoading}
        isNodeVisible={isPointCloudNodeVisible}
        onVisibilityToggle={(nodeId) => {
          if (nodeId === ROOT_NODE_ID) return;
          if (props.multiSelect) {
            selection.onVisibilityToggle(nodeId);
          } else {
            props.onVisibilityToggle(nodeId);
          }
        }}
        onCancelVisibilityToggle={(nodeId) => {
          if (nodeId !== ROOT_NODE_ID) {
            props.onCancelVisibilityToggle?.(nodeId);
          }
        }}
        {...selectionProps()}
      />
    </div>
  );
};
